import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  Button,
  Select,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Spin,
  notification,
  Divider,
  Switch,
} from "antd";
import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  getTransactionById,
  getPartnerInvoices,
  getFirstPagePartnerInvoicesForPayment,
  allocatePayment,
  getAllPartners,
  clearAccountingCache,
  clearPartnerInvoicesCache,
} from "../../../api/accounting";
import { invalidateCacheByPrefix } from "../../../context/AccountingDataCache";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Utils/BankStatements.css"; // reusing the same CSS for consistent styling
// Import column definitions
import getTransactionColumns from "../Columns/PaymentAllocation/transactionColumns";
import getInvoiceColumns from "../Columns/PaymentAllocation/invoiceColumns";
// Import the proper CreateInvoiceModal for creating bills/invoices
import CreateInvoiceModal from "../Forms/CreateInvoiceModal";

const { Text, Title } = Typography;
const { Option } = Select;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Helper to parse numbers that may contain commas, dollar signs, or other formatting
// parseFloat("18,132") returns 18, but we want 18132
// parseFloat("$20,670.60") returns NaN, but we want 20670.60
const parseNumberWithCommas = (value) => {
  if (value == null || value === undefined || value === "") return 0;
  // Convert to string and remove currency symbols ($, €, £, etc.), commas, and spaces
  const cleanedValue = String(value)
    .replace(/[$€£¥₹,\s]/g, "") // Remove currency symbols, commas, and spaces
    .trim();
  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? 0 : parsed;
};

// Get global display currency settings
const getGlobalDisplayCurrency = () => {
  try {
    const currency = localStorage.getItem("global_display_currency") || "USD";
    const SYMBOLS = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      CAD: "C$",
      AUD: "A$",
      CHF: "CHF",
      INR: "₹",
    };
    const symbol = SYMBOLS[currency] || currency;
    if (currency === "USD") return { currency, symbol, rate: 1 };
    const ratesStr = localStorage.getItem("custom_currency_rates");
    const rates = ratesStr ? JSON.parse(ratesStr) : {};
    return { currency, symbol, rate: rates[currency] || null };
  } catch (e) {
    return { currency: "USD", symbol: "$", rate: 1 };
  }
};

// Format amount using global display currency
const formatDisplayCurrency = (amount) => {
  if (amount == null || amount === undefined) return `$${(0).toFixed(getDecimalPrecision())}`;
  const value = parseFloat(amount);
  const {
    currency: displayCurrency,
    symbol: displaySymbol,
    rate,
  } = getGlobalDisplayCurrency();
  const dp = getDecimalPrecision();
  if (displayCurrency !== "USD" && rate && rate > 0) {
    const converted = value * rate;
    return `${displaySymbol}${converted.toLocaleString("en-US", {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    })}`;
  }
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;
};

const PaymentAllocation = ({
  transactionId,
  selectedBrand, // Can be single brand object OR array of brands for multi-brand allocation
  selectedBrands: selectedBrandsProp, // New prop for explicit multi-brand support
  onSuccess,
  onCancel,
  onViewPartner,
}) => {
  // Support both single brand and multiple brands
  // If selectedBrandsProp is provided, use it; otherwise convert selectedBrand to array
  const selectedBrands =
    selectedBrandsProp || (selectedBrand ? [selectedBrand] : []);
  const primaryBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;
  const navigate = useNavigate();
  const [loadingTransaction, setLoadingTransaction] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  // Background loading state for hybrid loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const backgroundLoadingRef = useRef(false);
  const backgroundAbortRef = useRef(false);
  // State for CreateInvoiceModal (used for both bills and invoices)
  const [createInvoiceModalVisible, setCreateInvoiceModalVisible] =
    useState(false);
  const [createInvoiceType, setCreateInvoiceType] = useState("bill"); // "bill" or "invoice"

  // Filter options for invoice columns
  const [filterOptions, setFilterOptions] = useState({
    status: [
      { text: "Paid", value: "paid" },
      { text: "Draft", value: "draft" },
    ],
    invoiceNumbers: [],
    partners: [],
    paymentTerms: [],
    brands: [],
  });
  const [showPaidInvoices, setShowPaidInvoices] = useState(false);
  // Toggle to show all partners (bypass vendor/customer filter)
  const [showAllPartners, setShowAllPartners] = useState(false);

  // Extract filter options from current invoice data (avoid additional API calls)
  const extractFilterOptionsFromData = (invoiceData) => {
    if (!invoiceData || invoiceData.length === 0) {
      return {
        invoiceNumbers: [],
        partners: [],
        paymentTerms: [],
        brands: [],
        status: [
          { text: "Paid", value: "paid" },
          { text: "Draft", value: "draft" },
        ],
      };
    }

    try {
      // Extract unique values for each filter from current data
      const invoiceNumbers = [
        ...new Set(invoiceData.map((inv) => inv.number).filter(Boolean)),
      ];
      const partners = [
        ...new Set(invoiceData.map((inv) => inv.partner_name).filter(Boolean)),
      ];
      const paymentTerms = [
        ...new Set(
          invoiceData.map((inv) => inv.payment_terms_code).filter(Boolean)
        ),
      ];
      const brandNames = [
        ...new Set(
          invoiceData.map((inv) => inv.brand_name || inv.brand?.name).filter(Boolean)
        ),
      ];
      const statuses =
        showPaidInvoices === true
          ? [...new Set(invoiceData.map((inv) => inv.status).filter(Boolean))]
          : [
              ...new Set(
                invoiceData
                  .map((inv) => inv.status)
                  .filter((status) => status !== "paid")
              ),
            ];

      // Update filter options
      return {
        invoiceNumbers: invoiceNumbers.map((num) => ({
          text: num,
          value: num,
        })),
        partners: partners.map((partner) => ({
          text: partner,
          value: partner,
        })),
        paymentTerms: paymentTerms.map((term) => ({ text: term, value: term })),
        brands: brandNames.map((name) => ({ text: name, value: name })),
        status: statuses.map((status) => ({
          text: status.charAt(0).toUpperCase() + status.slice(1),
          value: status,
        })),
      };
    } catch (error) {
      console.error("Error extracting filter options from data:", error);
      return {
        invoiceNumbers: [],
        partners: [],
        paymentTerms: [],
        brands: [],
        status: [
          { text: "Paid", value: "paid" },
          { text: "Draft", value: "draft" },
        ],
      };
    }
  };

  // Preload exchange rates on component mount
  useEffect(() => {
    // preloadExchangeRates().catch((err) => {
    //   console.warn("Failed to preload exchange rates:", err);
    // });
  }, []);

  useEffect(() => {
    if (!selectedPartner || selectedPartner.length === 0) {
      setFilterOptions({
        invoiceNumbers: [],
        partners: [],
        paymentTerms: [],
        brands: [],
        status: [
          { text: "Paid", value: "paid" },
          { text: "Draft", value: "draft" },
        ],
      });
      return;
    }
  }, [selectedPartner]);

  // Fetch transaction details
  const fetchTransaction = useCallback(async () => {
    setLoadingTransaction(true);
    try {
      const data = await getTransactionById(transactionId);
      setTransaction(data);

      // Transaction data now includes payment_allocations, allocated_amount, and allocation_status
      // directly from the backend
      if (data.payment_allocations) {
        setAllocations(data.payment_allocations);
      }

      // Update allocation status using data from backend
      updateAllocationStatus(data);

      // If transaction has a partner, pre-select it (useEffect will handle fetching invoices)
      // Handle case where partner might be an object with id property
      if (data.partner) {
        let partnerId = data.partner;
        if (typeof partnerId === "object") {
          partnerId = partnerId.id || partnerId.partner_id;
        }
        setSelectedPartner([partnerId]);
      }
    } catch (error) {
      console.error("Error fetching transaction:", error);

      // Check if it's a 404 error (transaction not found)
      if (error.response && error.response.status === 404) {
        notification.error({
          message: "Transaction Not Found",
          description: `Transaction with ID ${transactionId} does not exist.`,
        });
        // Navigate back to bank statements after a delay
        setTimeout(() => {
          handleBack();
        }, 2000);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load transaction details",
        });
      }
    } finally {
      setLoadingTransaction(false);
    }
  }, [transactionId]);

  // Fetch transaction data
  useEffect(() => {
    if (!transactionId) return;
    fetchTransaction();
  }, [transactionId, fetchTransaction]);

  // Store all partners (unfiltered) for when transaction loads
  const [allPartnersCache, setAllPartnersCache] = useState([]);

  // Fetch all partners as soon as selectedBrands is available (don't wait for transaction)
  useEffect(() => {
    if (!selectedBrands || selectedBrands.length === 0) {
      setPartners([]);
      setAllPartnersCache([]);
      return;
    }

    const fetchPartners = async () => {
      try {
        // Fetch partners for all selected brands
        const brandIds = selectedBrands.map((b) => b.id).join(",");
        const data = await getAllPartners({
          brand: brandIds,
        });

        // Deduplicate partners by ID first, then by name (same partner can exist across multiple brands)
        const rawPartnersArray = Array.isArray(data.data) ? data.data : [];
        const seenIds = new Set();
        const seenNames = new Set();
        const partnersArray = rawPartnersArray.filter((partner) => {
          // Skip if we've seen this ID
          if (seenIds.has(partner.id)) {
            return false;
          }
          // Skip if we've seen this exact name (for duplicate partner records)
          const normalizedName = (partner.name || "").toLowerCase().trim();
          if (seenNames.has(normalizedName)) {
            return false;
          }
          seenIds.add(partner.id);
          seenNames.add(normalizedName);
          return true;
        });
        setAllPartnersCache(partnersArray);

        // If transaction is already loaded, filter partners
        if (transaction) {
          const transactionAmount = parseFloat(transaction.amount || 0);
          let partnerTypeFilter = null;
          if (transactionAmount > 0) {
            partnerTypeFilter = ["customer", "both"];
          } else if (transactionAmount < 0) {
            partnerTypeFilter = ["vendor", "both"];
          }

          if (partnerTypeFilter) {
            const filtered = partnersArray.filter((partner) =>
              partnerTypeFilter.includes(partner.partner_type)
            );
            setPartners(filtered);
          } else {
            setPartners(partnersArray);
          }
        } else {
          // Transaction not loaded yet, show all partners
          setPartners(partnersArray);
        }
      } catch (error) {
        console.error("Error fetching partners:", error);
        setPartners([]);
        setAllPartnersCache([]);
      }
    };

    fetchPartners();
  }, [selectedBrands]); // Only depend on selectedBrands, not transaction

  // Re-filter partners when transaction loads (to filter by vendor/customer based on amount)
  // UNLESS showAllPartners is true - then show all partners
  useEffect(() => {
    if (!transaction || allPartnersCache.length === 0) return;

    // If "Show All" is enabled, skip filtering
    if (showAllPartners) {
      setPartners(allPartnersCache);
      return;
    }

    const transactionAmount = parseFloat(transaction.amount || 0);
    let partnerTypeFilter = null;
    if (transactionAmount > 0) {
      partnerTypeFilter = ["customer", "both"];
    } else if (transactionAmount < 0) {
      partnerTypeFilter = ["vendor", "both"];
    }

    if (partnerTypeFilter) {
      const filtered = allPartnersCache.filter((partner) =>
        partnerTypeFilter.includes(partner.partner_type)
      );
      setPartners(filtered);
    } else {
      setPartners(allPartnersCache);
    }
  }, [transaction, allPartnersCache, showAllPartners]);

  // State for sorting and filtering
  const [filters, setFilters] = useState({});
  const [sorter, setSorter] = useState({});

  // Helper to process invoice data
  const processInvoiceData = useCallback(
    (data) => {
      return data.map((invoice) => {
        const resolvedAllocationId =
          invoice.current_allocation_id ||
          invoice.allocation_id ||
          invoice.payment_allocation_id ||
          invoice.current_payment_allocation_id ||
          (invoice.current_allocation && invoice.current_allocation.id) ||
          (allocations.find(
            (a) =>
              a.invoice === invoice.id ||
              a.invoice_id === invoice.id ||
              a.invoice_number === invoice.number
          )?.id ??
            null);
        const currentAllocationId = resolvedAllocationId || null;

        const currentAllocationAmountUSD = parseFloat(
          invoice.current_allocation_amount || invoice.allocation_amount || 0
        );
        const totalAmountUSD = parseFloat(invoice.total_amount || 0);
        const paidAmountUSD = parseFloat(invoice.paid_amount || 0);

        let remainingAmountUSD = totalAmountUSD - paidAmountUSD;

        if (currentAllocationAmountUSD > 0 && currentAllocationId) {
          if (
            remainingAmountUSD < 0.01 &&
            currentAllocationAmountUSD < totalAmountUSD
          ) {
            remainingAmountUSD = totalAmountUSD - currentAllocationAmountUSD;
          } else if (
            remainingAmountUSD < 0.01 &&
            Math.abs(paidAmountUSD - currentAllocationAmountUSD) < 0.01
          ) {
            remainingAmountUSD =
              totalAmountUSD - paidAmountUSD + currentAllocationAmountUSD;
          }
        }

        remainingAmountUSD = Math.max(
          0,
          Math.min(remainingAmountUSD, totalAmountUSD)
        );

        const hasAllocation = !!currentAllocationId;
        const hasPaymentAmount = currentAllocationAmountUSD > 0;

        return {
          ...invoice,
          paymentAmount:
            currentAllocationAmountUSD > 0 ? currentAllocationAmountUSD : "",
          allocationId: currentAllocationId,
          hasExistingAllocation: !!currentAllocationId,
          paymentNotes: "",
          totalAmount: totalAmountUSD,
          paidAmount: paidAmountUSD,
          remainingAmount: remainingAmountUSD,
          is_allocated: !!currentAllocationId,
          _shouldSelect: hasAllocation && hasPaymentAmount,
        };
      });
    },
    [allocations]
  );

  // Fetch invoices function - HYBRID LOADING: first page fast, rest in background
  const fetchInvoices = async (
    partnerId,
    page = 1,
    pageSize = 100,
    filterParams = filters,
    sortParams = sorter,
    showPaidInvoices = false
  ) => {
    if (
      !partnerId ||
      !selectedBrands ||
      selectedBrands.length === 0 ||
      !transactionId
    ) {
      setLoadingInvoices(false);
      return;
    }

    // Abort any ongoing background loading
    backgroundAbortRef.current = true;
    setLoadingInvoices(true);

    try {
      const brandIds = selectedBrands.map((b) => b.id).join(",");
      const baseParams = {
        partner: partnerId,
        transaction_id: transactionId,
        brand: brandIds,
        show_paid_invoices: showPaidInvoices,
      };
      Object.entries(filterParams || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          baseParams[`${key}__in`] = Array.isArray(value)
            ? value.join(",")
            : value;
        }
      });

      if (sortParams && sortParams.field) {
        baseParams.ordering =
          sortParams.order === "descend"
            ? `-${sortParams.field}`
            : sortParams.field;
      }

      // ========== PHASE 1: Quick first page load ==========
      const firstPageResult = await getFirstPagePartnerInvoicesForPayment(
        baseParams
      );
      const firstPageData = firstPageResult.results || [];

      // Process and display first page immediately
      const processedFirstPage = processInvoiceData(firstPageData);
      setInvoices(processedFirstPage);

      // Update selected invoice IDs for allocated invoices
      const allocatedIds = processedFirstPage
        .filter((inv) => inv._shouldSelect)
        .map((inv) => inv.id);
      if (allocatedIds.length > 0) {
        setSelectedInvoiceIds((prev) => [
          ...new Set([...prev, ...allocatedIds]),
        ]);
      }

      // Update filter options from first page
      const newFilterOptions = extractFilterOptionsFromData(firstPageData);
      setFilterOptions(newFilterOptions);

      setPagination((prev) => ({
        ...prev,
        current: 1,
        pageSize: pageSize,
        total: firstPageResult.totalCount || firstPageData.length,
      }));

      setLoadingInvoices(false);

      // ========== PHASE 2: Load remaining data in background ==========
      if (firstPageResult.hasMore) {
        backgroundAbortRef.current = false;
        backgroundLoadingRef.current = true;
        setIsLoadingMore(true);

        setTimeout(async () => {
          try {
            const fullResponse = await getPartnerInvoices(baseParams);

            if (backgroundAbortRef.current) {
              backgroundLoadingRef.current = false;
              setIsLoadingMore(false);
              return;
            }

            const fullData = fullResponse.results || [];
            const total = fullResponse.count || fullData.length;

            const processedFull = processInvoiceData(fullData);
            setInvoices(processedFull);

            const allAllocatedIds = processedFull
              .filter((inv) => inv._shouldSelect)
              .map((inv) => inv.id);
            if (allAllocatedIds.length > 0) {
              setSelectedInvoiceIds((prev) => [
                ...new Set([...prev, ...allAllocatedIds]),
              ]);
            }

            // Update filter options from full data
            const fullFilterOptions = extractFilterOptionsFromData(fullData);
            setFilterOptions(fullFilterOptions);

            setPagination((prev) => ({
              ...prev,
              total: total,
            }));
          } catch (bgError) {
            console.error("Background loading error:", bgError);
          } finally {
            backgroundLoadingRef.current = false;
            setIsLoadingMore(false);
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error in fetchInvoices:", error);
      setInvoices([]);
      setLoadingInvoices(false);

      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch invoices. Please try again.";

      notification.error({
        message: "Error",
        description: errorMessage,
        duration: 5,
      });
    }
  };

  // Fetch invoices when partner changes or filters change (fetch ALL invoices, use client-side pagination)
  useEffect(() => {
    if (selectedPartner && selectedPartner.length > 0) {
      if (selectedPartner.length === 1) {
        fetchInvoices(
          selectedPartner[0],
          1,
          100,
          filters,
          sorter,
          showPaidInvoices
        );
      } else {
        // Fetch invoices for all selected partners and merge
        const fetchAllPartners = async () => {
          setLoadingInvoices(true);
          try {
            const results = await Promise.all(
              selectedPartner.map((pid) => {
                const brandIds = selectedBrands.map((b) => b.id).join(",");
                return getPartnerInvoices({
                  partner: pid,
                  transaction_id: transactionId,
                  brand: brandIds,
                  show_paid_invoices: showPaidInvoices,
                });
              })
            );
            const allInvoices = results.flatMap((r) => r.results || []);
            const processed = processInvoiceData(allInvoices);
            setInvoices(processed);
            const mergedFilterOptions = extractFilterOptionsFromData(allInvoices);
            setFilterOptions(mergedFilterOptions);
            setPagination((prev) => ({ ...prev, current: 1, total: processed.length }));
          } catch (error) {
            console.error("Error fetching invoices for multiple partners:", error);
            setInvoices([]);
          } finally {
            setLoadingInvoices(false);
          }
        };
        fetchAllPartners();
      }
    }
  }, [selectedPartner, selectedBrands, filters, sorter, showPaidInvoices]);

  // Handle partner change (value is now an array from multi-select)
  const handlePartnerChange = (value) => {
    setSelectedPartner(value || []);
    setInvoices([]);
  };

  // Handle payment allocation
  const handleAllocatePayment = async (
    invoiceId,
    amount,
    allocationId = null
  ) => {
    // Find the invoice to get its number for messages
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    const invoiceNumber = invoice?.number || invoiceId;

    // Convert amount to number if it's a string (handle commas)
    const paymentAmount =
      typeof amount === "string" ? parseNumberWithCommas(amount) : amount;

    // Check if this is a deallocation (amount is zero)
    const isDeallocation = paymentAmount === 0;

    // For allocation (not deallocation), validate amount is positive
    if (
      !isDeallocation &&
      (!paymentAmount || paymentAmount <= 0 || isNaN(paymentAmount))
    ) {
      notification.error({
        message: "Validation Error",
        description: "Please enter a valid payment amount greater than 0",
      });
      return;
    }

    // Find the invoice to validate against remaining amount
    const invoiceToAllocate = invoices.find((inv) => inv.id === invoiceId);
    if (!invoiceToAllocate) {
      notification.error({
        message: "Validation Error",
        description: "Invoice not found",
      });
      return;
    }

    // Prevent allocation to cancelled invoices
    if (!isDeallocation && invoiceToAllocate.status === "cancelled") {
      notification.error({
        message: "Validation Error",
        description: `Cannot allocate payment to cancelled invoice #${
          invoiceToAllocate.number || invoiceNumber
        }. Cancelled invoices cannot receive allocations.`,
      });
      return;
    }

    // Only validate remaining amount for new allocations (not deallocations)
    if (!isDeallocation) {
      // All amounts are already in USD (converted when fetching invoices)
      // paymentAmount is in USD (what user entered)
      const paymentAmountUSD = parseNumberWithCommas(paymentAmount); // Handle comma-formatted numbers
      const remainingAmountUSD = parseFloat(
        invoiceToAllocate.remainingAmount || 0
      ); // Already in USD

      const epsilon = 0.001; // Small tolerance for floating point comparison

      // If updating an existing allocation, add its current amount to the remaining amount
      // existingPaymentAmount is also in USD
      let adjustedRemainingAmountUSD = remainingAmountUSD;
      if (allocationId && invoiceToAllocate.hasExistingAllocation) {
        const existingPaymentAmountUSD = parseFloat(
          invoiceToAllocate.paymentAmount || 0
        ); // Already in USD
        adjustedRemainingAmountUSD += existingPaymentAmountUSD;
      }

      if (paymentAmountUSD > adjustedRemainingAmountUSD + epsilon) {
        notification.error({
          message: "Allocation Error",
          description: `Invoice only has ${adjustedRemainingAmountUSD.toFixed(
            3
          )} USD remaining balance available for allocation.`,
        });
        return;
      }
    }

    // Validate transaction has enough unallocated amount
    if (transaction && !isDeallocation) {
      // Check if transaction is fully converted to overpayment
      // If remaining_amount is 0 or very small, the transaction cannot be allocated
      const transactionRemaining = parseFloat(
        transaction.remaining_amount || 0
      );
      const epsilon = 0.001;

      if (Math.abs(transactionRemaining) < epsilon) {
        // Provide more helpful error message
        const overpaymentAmt = parseFloat(transaction.overpayment_amount || 0);
        const hasOverpayment = overpaymentAmt > 0;

        notification.error({
          message: "Allocation Not Allowed",
          description: hasOverpayment
            ? `This transaction has ${formatDisplayCurrency(
                overpaymentAmt
              )} converted to overpayment(s). To allocate to invoices, first delete the overpayment(s) using the O-Pay button, then retry.`
            : "This transaction has no remaining amount to allocate. It may have been fully allocated or converted to overpayment.",
        });
        return;
      }

      // Always use absolute value of transaction amount for validation
      // This is critical for negative transactions (bills/payments to vendors)
      const transactionAmount = Math.abs(parseFloat(transaction.amount || 0));
      const allocatedAmount = parseFloat(transaction.allocated_amount || 0);
      const overpaymentAmount = parseFloat(transaction.overpayment_amount || 0);

      // Calculate remaining amount considering both allocations and overpayments
      // remaining_amount property already accounts for both, but we'll use it directly
      const remainingTransactionAmount = Math.abs(transactionRemaining);

      // CRITICAL: All amounts in database are stored in USD
      // Transaction remaining_amount is already in USD from backend
      // No conversion needed - backend handles all currency conversion on save
      const remainingTransactionAmountUSD = remainingTransactionAmount;

      // paymentAmount is already in USD (what user entered)
      const paymentAmountUSD = parseNumberWithCommas(paymentAmount); // Handle comma-formatted numbers

      // If updating an existing allocation, add its current amount to the remaining amount
      // existingPaymentAmount is also in USD
      let adjustedRemainingAmountUSD = remainingTransactionAmountUSD;
      if (allocationId && invoiceToAllocate.hasExistingAllocation) {
        const existingPaymentAmountUSD = parseFloat(
          invoiceToAllocate.paymentAmount || 0
        ); // Already in USD
        adjustedRemainingAmountUSD += existingPaymentAmountUSD;
      }

      // Compare in USD
      if (paymentAmountUSD > adjustedRemainingAmountUSD + epsilon) {
        notification.error({
          message: "Validation Error",
          description: `Payment amount (${paymentAmountUSD.toFixed(
            3
          )} USD) exceeds the remaining transaction amount (${adjustedRemainingAmountUSD.toFixed(
            3
          )} USD)`,
        });
        return;
      }
    }

    try {
      setLoadingAllocation(true);

      // Handle deallocation - use allocate_payment with amount=0 and allocation_id
      if (isDeallocation) {
        // Find the allocation ID from multiple sources
        const existingAllocation = allocations.find(
          (a) =>
            a.invoice === invoiceId ||
            a.invoice_id === invoiceId ||
            a.invoice_number === invoiceNumber
        );

        // Try multiple sources for allocation ID
        const allocationIdToDelete =
          allocationId ||
          existingAllocation?.id ||
          invoiceToAllocate?.allocationId ||
          invoiceToAllocate?.allocation_id ||
          invoiceToAllocate?.current_allocation_id;

        if (!allocationIdToDelete) {
          notification.error({
            message: "Unallocation Error",
            description:
              "Cannot find allocation to remove. The allocation may have already been removed.",
          });
          setLoadingAllocation(false);
          return;
        }

        const allocationAmountFromArray = existingAllocation
          ? parseFloat(
              existingAllocation.amount ||
                existingAllocation.payment_amount ||
                0
            )
          : parseNumberWithCommas(
              invoiceToAllocate?.currentAllocationAmount ||
                invoiceToAllocate?.paymentAmount ||
                0
            );

        // Call allocate_payment with amount=0 and allocation_id to delete the allocation
        await allocatePayment(transactionId, {
          transaction_id: transactionId,
          invoice_id: invoiceId,
          amount: 0,
          allocation_id: allocationIdToDelete,
          partner_id: (() => {
            const p = invoiceToAllocate?.partner_id || invoiceToAllocate?.partner || (selectedPartner.length === 1 ? selectedPartner[0] : null);
            return (typeof p === 'object' && p !== null) ? p.id : p;
          })(),
        });

        // For unallocation, immediately update the UI state
        // This ensures the button changes from Unallocate to Allocate right away
        setInvoices((prevInvoices) => {
          return prevInvoices.map((inv) => {
            if (inv.id === invoiceId) {
              // Get the allocation amount - try multiple sources
              // Use parseNumberWithCommas for paymentAmount as it could be user-entered with commas
              const previousAllocationAmount =
                allocationAmountFromArray ||
                parseFloat(inv.currentAllocationAmount || 0) ||
                parseNumberWithCommas(inv.paymentAmount || 0) ||
                parseFloat(inv.current_allocation_amount || 0) ||
                parseFloat(inv.allocation_amount || 0);

              const currentPaid = parseFloat(
                inv.paidAmount || inv.paid_amount || 0
              );
              const totalAmount = parseFloat(
                inv.totalAmount || inv.total_amount || 0
              );

              // Subtract the unallocated amount from paid
              const newPaidAmount = Math.max(
                0,
                currentPaid - previousAllocationAmount
              );
              const newRemainingAmount = Math.max(
                0,
                totalAmount - newPaidAmount
              );

              return {
                ...inv,
                hasExistingAllocation: false,
                allocationId: null,
                paymentAmount: "", // Empty for free typing after unallocation
                currentAllocationAmount: 0,
                current_allocation_amount: 0,
                paidAmount: newPaidAmount,
                paid_amount: newPaidAmount,
                remainingAmount: newRemainingAmount,
                status:
                  newPaidAmount <= 0
                    ? "open"
                    : newPaidAmount >= totalAmount
                    ? "paid"
                    : "partial",
              };
            }
            return inv;
          });
        });

        // Update allocations array to remove this allocation
        setAllocations((prevAllocations) =>
          prevAllocations.filter(
            (alloc) =>
              !(alloc.invoice === invoiceId || alloc.invoice_id === invoiceId)
          )
        );

        // Update transaction header locally for immediate UI feedback
        // Use parseNumberWithCommas for paymentAmount as it could be user-entered with commas
        const unallocatedAmount =
          allocationAmountFromArray ||
          parseFloat(invoiceToAllocate.currentAllocationAmount || 0) ||
          parseNumberWithCommas(invoiceToAllocate.paymentAmount || 0);

        setTransaction((prevTransaction) => {
          if (!prevTransaction) return prevTransaction;
          const transactionAmount = Math.abs(
            parseFloat(prevTransaction.amount || 0)
          );
          const currentAllocated = parseFloat(
            prevTransaction.allocated_amount || 0
          );
          const newAllocated = Math.max(
            0,
            currentAllocated - unallocatedAmount
          );
          const newRemaining = Math.max(0, transactionAmount - newAllocated);
          const newPercentAllocated =
            transactionAmount > 0
              ? (newAllocated / transactionAmount) * 100
              : 0;

          return {
            ...prevTransaction,
            allocated_amount: newAllocated,
            remaining_amount: newRemaining,
            percent_allocated: newPercentAllocated,
          };
        });

        // Update allocation status display
        setAllocationStatus((prevStatus) => {
          const transactionAmount = Math.abs(
            parseFloat(transaction?.amount || 0)
          );
          const currentAllocated = parseFloat(
            transaction?.allocated_amount || 0
          );
          const newAllocated = Math.max(
            0,
            currentAllocated - unallocatedAmount
          );
          const newPercentAllocated =
            transactionAmount > 0
              ? (newAllocated / transactionAmount) * 100
              : 0;

          let statusType = "unallocated";
          let statusLabel = "Unallocated";
          let statusColor = "default";

          if (newPercentAllocated >= 99.99) {
            statusType = "fully_allocated";
            statusLabel = "Fully Allocated";
            statusColor = "success";
          } else if (newPercentAllocated > 0) {
            statusType = "partially_allocated";
            statusLabel = "Partially Allocated";
            statusColor = "warning";
          }

          return {
            type: statusType,
            label: statusLabel,
            color: statusColor,
            totalAllocated: newAllocated,
            percentAllocated: newPercentAllocated,
          };
        });

        const unallocatedAmountDisplay =
          unallocatedAmount > 0
            ? ` (${formatDisplayCurrency(unallocatedAmount)})`
            : "";
        notification.success({
          message: "Payment Unallocated",
          description: `Successfully unallocated payment${unallocatedAmountDisplay} from invoice #${invoiceNumber}`,
        });

        // Clear cache for deallocation
        clearAccountingCache();
        clearPartnerInvoicesCache();
      } else {
        // This is an ALLOCATION (not deallocation) - call the allocate endpoint
        // CRITICAL: paymentAmount is already in USD (user entered USD, all displays are USD)
        // Backend expects USD, so use amount as-is
        const amountToAllocate = parseNumberWithCommas(paymentAmount);

        // Prepare allocation data
        const invoiceRecord = invoices.find((inv) => inv.id === invoiceId);
        const allocationData = {
          transaction_id: transactionId,
          invoice_id: invoiceId,
          amount: amountToAllocate,
          partner_id: (() => {
            const p = invoiceRecord?.partner_id || invoiceRecord?.partner || (selectedPartner.length === 1 ? selectedPartner[0] : null);
            return (typeof p === 'object' && p !== null) ? p.id : p;
          })(),
        };

        // If this is updating an existing allocation, include the allocation_id
        if (allocationId) {
          allocationData.allocation_id = allocationId;
        }

        const result = await allocatePayment(transactionId, allocationData);

        notification.success({
          message: "Payment Allocated",
          description: `Successfully ${
            allocationId ? "updated" : "allocated"
          } ${formatDisplayCurrency(
            parseNumberWithCommas(paymentAmount)
          )} to invoice #${invoiceNumber}`,
        });

        // Immediately update local invoice state with the new allocation amount
        // This ensures the Payment Amount column shows the correct value
        const allocatedInvoiceId = invoiceId;
        // Use parseNumberWithCommas to handle comma-formatted numbers like "18,132"
        const allocatedAmount = parseNumberWithCommas(paymentAmount);

        // Get allocation ID from result - check multiple possible locations
        let newAllocationId = result.allocation_id || result.id || allocationId;

        // If not found in direct result, try to find from transaction's payment_allocations
        if (!newAllocationId && result.transaction?.payment_allocations) {
          const matchingAlloc = result.transaction.payment_allocations.find(
            (a) => a.invoice === invoiceId || a.invoice_id === invoiceId
          );
          newAllocationId = matchingAlloc?.id;
        }

        setInvoices((prevInvoices) => {
          return prevInvoices.map((inv) => {
            if (inv.id === allocatedInvoiceId) {
              // Get current values, handling both camelCase and snake_case
              const currentPaid = parseFloat(
                inv.paidAmount || inv.paid_amount || 0
              );
              const totalAmount = parseFloat(
                inv.totalAmount || inv.total_amount || 0
              );
              const previousAllocation = parseFloat(
                inv.currentAllocationAmount || 0
              );

              // If updating existing allocation, don't double-add
              let newPaidAmount;
              if (allocationId && previousAllocation > 0) {
                // Updating: replace old allocation with new
                newPaidAmount =
                  currentPaid - previousAllocation + allocatedAmount;
              } else {
                // New allocation: add to existing paid
                newPaidAmount = currentPaid + allocatedAmount;
              }

              const newRemainingAmount = Math.max(
                0,
                totalAmount - newPaidAmount
              );

              return {
                ...inv,
                hasExistingAllocation: true,
                allocationId: newAllocationId,
                paymentAmount: allocatedAmount,
                currentAllocationAmount: allocatedAmount,
                current_allocation_amount: allocatedAmount,
                paidAmount: newPaidAmount,
                paid_amount: newPaidAmount,
                remainingAmount: newRemainingAmount,
                // Update status based on paid amount
                status:
                  newPaidAmount >= totalAmount
                    ? "paid"
                    : newPaidAmount > 0
                    ? "partial"
                    : "open",
              };
            }
            return inv;
          });
        });

        // Also update the allocations array with the new allocation
        const previousAllocationForInvoice = allocations.find(
          (a) =>
            a.invoice === allocatedInvoiceId ||
            a.invoice_id === allocatedInvoiceId
        );
        const previousAmountForInvoice = previousAllocationForInvoice
          ? parseFloat(
              previousAllocationForInvoice.amount ||
                previousAllocationForInvoice.payment_amount ||
                0
            )
          : 0;

        setAllocations((prevAllocations) => {
          // Remove any existing allocation for this invoice
          const filtered = prevAllocations.filter(
            (a) =>
              !(
                a.invoice === allocatedInvoiceId ||
                a.invoice_id === allocatedInvoiceId
              )
          );
          // Add the new allocation
          return [
            ...filtered,
            {
              id: newAllocationId,
              invoice: allocatedInvoiceId,
              invoice_id: allocatedInvoiceId,
              invoice_number: invoiceNumber,
              amount: allocatedAmount,
              payment_amount: allocatedAmount,
            },
          ];
        });

        // Update transaction header locally for immediate UI feedback
        setTransaction((prevTransaction) => {
          if (!prevTransaction) return prevTransaction;
          const transactionAmount = Math.abs(
            parseFloat(prevTransaction.amount || 0)
          );
          const currentAllocated = parseFloat(
            prevTransaction.allocated_amount || 0
          );
          // Calculate new allocated: add new allocation, subtract previous allocation for same invoice
          const newAllocated =
            currentAllocated - previousAmountForInvoice + allocatedAmount;
          const newRemaining = Math.max(0, transactionAmount - newAllocated);
          const newPercentAllocated =
            transactionAmount > 0
              ? (newAllocated / transactionAmount) * 100
              : 0;

          return {
            ...prevTransaction,
            allocated_amount: newAllocated,
            remaining_amount: newRemaining,
            percent_allocated: newPercentAllocated,
          };
        });

        // Update allocation status display
        setAllocationStatus((prevStatus) => {
          const transactionAmount = Math.abs(
            parseFloat(transaction?.amount || 0)
          );
          const currentAllocated = parseFloat(
            transaction?.allocated_amount || 0
          );
          const newAllocated =
            currentAllocated - previousAmountForInvoice + allocatedAmount;
          const newPercentAllocated =
            transactionAmount > 0
              ? (newAllocated / transactionAmount) * 100
              : 0;

          let statusType = "unallocated";
          let statusLabel = "Unallocated";
          let statusColor = "default";

          if (newPercentAllocated >= 99.99) {
            statusType = "fully_allocated";
            statusLabel = "Fully Allocated";
            statusColor = "success";
          } else if (newPercentAllocated > 0) {
            statusType = "partially_allocated";
            statusLabel = "Partially Allocated";
            statusColor = "warning";
          }

          return {
            type: statusType,
            label: statusLabel,
            color: statusColor,
            totalAllocated: newAllocated,
            percentAllocated: newPercentAllocated,
          };
        });

        // Clear cache to ensure fresh data on next page load
        clearAccountingCache();
        clearPartnerInvoicesCache();

        // The backend now returns the updated transaction with allocation data
        if (result && result.transaction) {
          setTransaction(result.transaction);

          // Update allocation status using data from backend
          // Pass true to skip overwriting our local allocations array
          updateAllocationStatus(result.transaction, true);
        }
        // Note: Don't call fetchTransaction() here - it would overwrite our local state updates
      }

      // Notify parent component with the transactionId so it can update just that row
      // instead of refreshing the entire list
      if (onSuccess) {
        onSuccess({ transactionId, type: "transaction" });
      }
    } catch (error) {
      console.error("Error allocating payment:", error);

      // Provide more specific error messages
      let errorMessage = "Failed to allocate payment";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
        errorMessage =
          "Invalid payment data. The amount may exceed the available transaction amount or invoice remaining balance.";
      } else if (error.response?.status === 404) {
        errorMessage = "Invoice or transaction not found.";
      } else if (error.response?.status === 403) {
        errorMessage = "You do not have permission to allocate this payment.";
      }

      notification.error({
        message: "Allocation Error",
        description: errorMessage,
      });
    } finally {
      setLoadingAllocation(false);
    }
  };

  const handlePaymentChange = (invoiceId, e) => {
    // Get the raw input value
    const inputValue = e.target.value;

    // Update the payment amount for the specific invoice
    const updatedInvoices = invoices.map((invoice) => {
      if (invoice.id === invoiceId) {
        // Store the raw input value to prevent cursor jumping
        // Only convert to number when actually submitting
        return {
          ...invoice,
          paymentAmount: inputValue,
        };
      }
      return invoice;
    });

    setInvoices(updatedInvoices);

    // Auto-check the invoice if a valid payment amount is entered
    // Use parseNumberWithCommas to handle comma-formatted numbers like "18,132"
    const numericValue = parseNumberWithCommas(inputValue);
    if (numericValue > 0) {
      // Add to selected invoices if not already selected
      if (!selectedInvoiceIds.includes(invoiceId)) {
        setSelectedInvoiceIds((prev) => [...prev, invoiceId]);
      }
    } else {
      // Remove from selected invoices if payment amount is cleared or invalid
      setSelectedInvoiceIds((prev) => prev.filter((id) => id !== invoiceId));
    }
  };

  // Handle row selection change (for multiple selection)
  const handleRowSelectionChange = (selectedRowKeys) => {
    setSelectedInvoiceIds(selectedRowKeys);
  };

  // Handle allocate all checked invoices
  const handleAllocateAllChecked = async () => {
    if (selectedInvoiceIds.length === 0) {
      notification.warning({
        message: "No Invoices Selected",
        description: "Please select at least one invoice to allocate payment.",
      });
      return;
    }

    setLoadingAllocation(true);

    try {
      // Process each selected invoice one by one
      for (const invoiceId of selectedInvoiceIds) {
        const invoice = invoices.find((inv) => inv.id === invoiceId);

        if (!invoice) continue;

        // Skip invoices that are already fully paid, cancelled, or have no remaining balance
        if (
          parseFloat(invoice.remainingAmount) <= 0 ||
          invoice.status === "paid" ||
          invoice.status === "cancelled"
        ) {
          continue;
        }

        // Get the allocation ID if it exists
        const allocationId = invoice.allocationId;

        // Use the entered payment amount if available, otherwise use the remaining amount
        let amount;
        // Use parseNumberWithCommas to handle comma-formatted numbers like "18,132"
        if (
          invoice.paymentAmount &&
          parseNumberWithCommas(invoice.paymentAmount) > 0
        ) {
          amount = parseNumberWithCommas(invoice.paymentAmount);
        } else {
          amount = parseFloat(invoice.remainingAmount);
        }

        // Allocate payment to this invoice
        await handleAllocatePayment(invoice.id, amount, allocationId);
      }
    } catch (error) {
      console.error("Error in bulk allocation:", error);
      notification.error({
        message: "Bulk Allocation Error",
        description:
          "An error occurred while allocating payment to multiple invoices.",
      });
    } finally {
      setLoadingAllocation(false);
    }
  };
  // Update allocation status - set skipAllocationsUpdate=true when we've already updated allocations locally
  const updateAllocationStatus = (
    transaction,
    skipAllocationsUpdate = false
  ) => {
    if (!transaction) {
      setAllocationStatus(null);
      return;
    }

    try {
      // Only update allocations from backend if we haven't already updated them locally
      if (transaction.payment_allocations && !skipAllocationsUpdate) {
        setAllocations(transaction.payment_allocations);
      }
      if (transaction.allocation_status) {
        setAllocationStatus({
          ...transaction.allocation_status,
          totalAllocated: parseFloat(transaction.allocated_amount || 0),
          percentAllocated: transaction.percent_allocated || 0,
        });
        return;
      }

      const transactionAmount = Math.abs(parseFloat(transaction.amount || 0));
      const totalAllocated = parseFloat(transaction.allocated_amount || 0);
      const percentAllocated =
        transactionAmount > 0 ? (totalAllocated / transactionAmount) * 100 : 0;
      // Determine status based on remaining amount (more accurate than percentage)
      const remainingAmount = transactionAmount - totalAllocated;
      let status = null;
      // Only mark as fully allocated if remaining is essentially 0
      if (remainingAmount <= 0.001) {
        status = {
          type: "fully_allocated",
          label: "Fully Allocated",
          color: "success",
        };
      } else if (totalAllocated > 0) {
        status = {
          type: "partially_allocated",
          label: "Partially Allocated",
          color: "warning",
        };
      } else {
        status = {
          type: "unallocated",
          label: "Unallocated",
          color: "default",
        };
      }

      setAllocationStatus({
        ...status,
        totalAllocated,
        percentAllocated,
      });
    } catch (error) {
      console.error("Error updating allocation status:", error);
      setAllocationStatus({
        type: "error",
        label: "Error",
        color: "error",
        totalAllocated: 0,
        percentAllocated: 0,
      });
    }
  };

  // Get transaction columns from the imported function
  const transactionColumns = getTransactionColumns(allocationStatus);

  // Sort invoices by oldest to newest (issue_date ascending) for allocation
  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const dateA = a.issue_date ? new Date(a.issue_date) : new Date(0);
      const dateB = b.issue_date ? new Date(b.issue_date) : new Date(0);
      return dateA - dateB; // Oldest first
    });
  }, [invoices]);

  // Calculate transaction's remaining amount that can still be allocated
  // This should exclude overpayment amounts - if fully converted to overpayment, remaining should be 0
  const transactionRemainingAmount = useMemo(() => {
    if (!transaction) return 0;

    // Use the remaining_amount property which already accounts for both allocations and overpayments
    const remaining = parseFloat(transaction.remaining_amount || 0);

    // If transaction is fully converted to overpayment, remaining should be 0 or very small
    // Return 0 in this case to prevent allocation
    const epsilon = 0.001;
    if (Math.abs(remaining) < epsilon) {
      return 0;
    }

    return Math.abs(remaining);
  }, [transaction]);

  // Get invoice columns with necessary props
  const invoiceColumns = getInvoiceColumns({
    invoices: sortedInvoices,
    allocations,
    handlePaymentChange,
    handleAllocatePayment,
    loadingAllocation,
    filterOptions,
    currentFilters: filters,
    transactionRemainingAmount, // Pass transaction remaining amount
    onViewPartner,
  });

  // Handle back button
  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/accounting/bank-statements");
    }
  };

  // Handle Create Bill button click - opens the CreateInvoiceModal with type "bill"
  const handleCreateBillClick = () => {
    if (!selectedPartner || selectedPartner.length === 0) {
      notification.error({
        message: "Error",
        description: "Please select a partner first",
      });
      return;
    }
    if (selectedPartner.length > 1) {
      notification.error({
        message: "Error",
        description: "Please select only one partner to create a bill",
      });
      return;
    }

    // Validate partner is a vendor
    const partner = partners.find((p) => p.id === selectedPartner[0]);
    if (
      partner &&
      partner.partner_type !== "vendor" &&
      partner.partner_type !== "both"
    ) {
      notification.error({
        message: "Error",
        description: "Bills can only be created for vendors",
      });
      return;
    }

    setCreateInvoiceType("bill");
    setCreateInvoiceModalVisible(true);
  };

  // Handle Create Invoice button click - opens the CreateInvoiceModal with type "invoice"
  const handleCreateInvoiceClick = () => {
    if (!selectedPartner || selectedPartner.length === 0) {
      notification.error({
        message: "Error",
        description: "Please select a partner first",
      });
      return;
    }
    if (selectedPartner.length > 1) {
      notification.error({
        message: "Error",
        description: "Please select only one partner to create an invoice",
      });
      return;
    }

    // Validate partner is a customer
    const partner = partners.find((p) => p.id === selectedPartner[0]);
    if (
      partner &&
      partner.partner_type !== "customer" &&
      partner.partner_type !== "both"
    ) {
      notification.error({
        message: "Error",
        description: "Invoices can only be created for customers",
      });
      return;
    }

    setCreateInvoiceType("invoice");
    setCreateInvoiceModalVisible(true);
  };

  // Handle successful bill/invoice creation from modal
  const handleCreateInvoiceSuccess = () => {
    notification.success({
      message: "Success",
      description: `${
        createInvoiceType === "bill" ? "Bill" : "Invoice"
      } created successfully!`,
    });

    // Close modal
    setCreateInvoiceModalVisible(false);

    // Clear cache to ensure fresh data
    clearAccountingCache();
    clearPartnerInvoicesCache();

    // Invalidate cache for AccountsPayable/AccountsReceivable
    if (createInvoiceType === "bill") {
      invalidateCacheByPrefix("bills_");
      invalidateCacheByPrefix("bill_credits_");
      invalidateCacheByPrefix("bill_history_");
      invalidateCacheByPrefix("payable_statistics_");
      // Dispatch event to notify AccountsPayable to refresh
      window.dispatchEvent(new CustomEvent("billCreated"));
    } else {
      invalidateCacheByPrefix("invoices_");
      invalidateCacheByPrefix("invoice_credits_");
      invalidateCacheByPrefix("invoice_history_");
      invalidateCacheByPrefix("receivable_statistics_");
      // Dispatch event to notify AccountsReceivable to refresh
      window.dispatchEvent(new CustomEvent("invoiceCreated"));
    }

    // Refresh invoices to show the newly created bill/invoice
    if (selectedPartner && selectedPartner.length === 1) {
      fetchInvoices(
        selectedPartner[0],
        pagination.current,
        pagination.pageSize,
        filters,
        sorter,
        showPaidInvoices
      );
    } else if (selectedPartner && selectedPartner.length > 1) {
      // Re-trigger the multi-partner fetch via state change
      setSelectedPartner([...selectedPartner]);
    }

    // Notify parent component with transactionId to update just that row
    if (onSuccess) {
      onSuccess({ transactionId, type: "transaction" });
    }
  };

  // Handle modal cancel
  const handleCreateInvoiceCancel = () => {
    setCreateInvoiceModalVisible(false);
  };

  const handleShowPaidInvoicesChange = (checked) => {
    setShowPaidInvoices(checked);
  };
  if (!transactionId || !selectedBrands || selectedBrands.length === 0) {
    return (
      <Card>
        <Text type="secondary">
          No transaction selected or brand not selected
        </Text>
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
          >
            Back to Bank Statements
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card size="small" className="payment-allocation-card">
      {loadingTransaction ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Title level={5}>Transaction Details</Title>
          {transaction && (
            <Table
              dataSource={[transaction]}
              columns={transactionColumns}
              pagination={false}
              rowKey="id"
              size="small"
            />
          )}

          <Divider />

          <Row gutter={16}>
            <Col span={8}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Select Partner:</Text>
                {transaction && transaction.amount > 0 ? (
                  <Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Credit transaction (money in) - showing customers only
                  </Text>
                ) : transaction && transaction.amount < 0 ? (
                  <Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Debit transaction (money out) - showing vendors only
                  </Text>
                ) : null}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontSize: 12, color: "#666" }}>
                    Show All Partners:
                  </span>
                  <Switch
                    size="small"
                    checked={showAllPartners}
                    onChange={(checked) => setShowAllPartners(checked)}
                  />
                  {showAllPartners && (
                    <Text type="warning" style={{ fontSize: 11 }}>
                      (Showing all partners - may include incompatible types)
                    </Text>
                  )}
                </div>
                <Select
                  mode="multiple"
                  placeholder="Search and select partners"
                  style={{ width: "100%" }}
                  onChange={handlePartnerChange}
                  value={selectedPartner}
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children;
                    if (typeof label === "string") {
                      return label.toLowerCase().includes(input.toLowerCase());
                    }
                    const text = String(label || "");
                    return text.toLowerCase().includes(input.toLowerCase());
                  }}
                  notFoundContent={
                    <div style={{ padding: 8, textAlign: "center" }}>
                      <p>
                        Partner not found in{" "}
                        {showAllPartners ? "all" : "filtered"} list.
                      </p>
                      {!showAllPartners && (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => setShowAllPartners(true)}
                        >
                          Show All Partners
                        </Button>
                      )}
                    </div>
                  }
                >
                  {Array.isArray(partners) && partners.length > 0 ? (
                    partners.map((partner) => (
                      <Option
                        key={partner.id}
                        value={partner.id}
                        label={`${partner.name} (${partner.partner_type})`}
                      >
                        {partner.name} ({partner.partner_type})
                      </Option>
                    ))
                  ) : (
                    <Option value="" disabled label="No partners available">
                      No partners available
                    </Option>
                  )}
                </Select>
              </div>
            </Col>
          </Row>

          <Row
            align="middle"
            justify="space-between"
            style={{ marginTop: 16, marginBottom: 16 }}
          >
            <Col>
              <Title level={5} style={{ margin: 0 }}>
                Partner Invoices
              </Title>
            </Col>
            <Col>
              <Space>
                {/* Show Create Bill button for negative (debit) transactions */}
                {transaction && transaction.amount < 0 && (
                  <Button
                    type="default"
                    onClick={handleCreateBillClick}
                    disabled={!selectedPartner || selectedPartner.length !== 1}
                    icon={<PlusOutlined />}
                  >
                    Create Bill
                  </Button>
                )}
                {/* Show Create Invoice button for positive (credit) transactions */}
                {transaction && transaction.amount > 0 && (
                  <Button
                    type="default"
                    onClick={handleCreateInvoiceClick}
                    disabled={!selectedPartner || selectedPartner.length !== 1}
                    icon={<PlusOutlined />}
                  >
                    Create Invoice
                  </Button>
                )}
                <Button
                  type="primary"
                  onClick={handleAllocateAllChecked}
                  disabled={
                    loadingAllocation || selectedInvoiceIds.length === 0
                  }
                  loading={loadingAllocation}
                >
                  Allocate All Checked
                </Button>
              </Space>
            </Col>
          </Row>
          <Row
            align="middle"
            justify="space-between"
            style={{ marginBottom: 16 }}
          >
            <Col>
              <Title level={5} style={{ margin: 0 }}></Title>
            </Col>
            <Col>
              <Space>
                <span>Show Paid</span>
                <Switch
                  checked={showPaidInvoices}
                  onChange={handleShowPaidInvoicesChange}
                />
              </Space>
            </Col>
          </Row>
          <div className="fixed-table-container">
            <Table
              dataSource={sortedInvoices}
              columns={invoiceColumns}
              filters={filters} // Pass filters to Table component
              pagination={{
                ...pagination,
                total: sortedInvoices.length,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ["50", "100", "200"],
                showTotal: (total) => `Total ${total} invoices`,
                onChange: (page, pageSize) => {
                  setPagination({ current: page, pageSize });
                },
                onShowSizeChange: (current, size) => {
                  setPagination({ current: 1, pageSize: size });
                },
              }}
              onChange={(pagination, filters, sorter) => {
                // Handle sorting
                if (Array.isArray(sorter)) {
                  // Handle multiple sorters
                  if (sorter.length > 0) {
                    setSorter({
                      field: sorter[0].field,
                      order: sorter[0].order,
                    });
                  } else {
                    setSorter({});
                  }
                } else {
                  // Handle single sorter
                  setSorter({
                    field: sorter.field,
                    order: sorter.order,
                  });
                }

                // Handle filtering - keep the original filter values for UI
                const newFilters = {};
                const apiFilters = {};

                Object.entries(filters).forEach(([key, value]) => {
                  if (value && value.length > 0) {
                    newFilters[key] = value; // Keep array for UI
                    apiFilters[key] = value.join(","); // Convert to string for API
                  }
                });

                setFilters(newFilters);

                // Fetch with new filters and sort
                if (selectedPartner && selectedPartner.length === 1) {
                  fetchInvoices(
                    selectedPartner[0],
                    pagination.current,
                    pagination.pageSize,
                    apiFilters,
                    Array.isArray(sorter) ? sorter[0] || {} : sorter
                  );
                }
              }}
              rowKey="id"
              size="small"
              loading={loadingInvoices}
              scroll={{ x: 1810, y: 400 }}
              tableLayout="auto"
              sticky={{ offsetHeader: 0 }}
              rowClassName={(record) =>
                record.is_allocated ? "table-success-outline-invoice" : ""
              }
              rowSelection={{
                selectedRowKeys: selectedInvoiceIds,
                onChange: handleRowSelectionChange,
                getCheckboxProps: (record) => {
                  // Allow allocation to draft invoices — it will be stored as "pending"
                  // and auto-activated when management approves
                  return {
                    disabled:
                      parseFloat(record.remainingAmount) <= 0 ||
                      record.status === "paid" ||
                      record.status === "cancelled",
                  };
                },
              }}
            />
          </div>
        </>
      )}

      {/* Create Bill/Invoice Modal - Uses the full CreateInvoiceModal component */}
      <CreateInvoiceModal
        visible={createInvoiceModalVisible}
        onCancel={handleCreateInvoiceCancel}
        onSuccess={handleCreateInvoiceSuccess}
        selectedBrand={primaryBrand}
        defaultInvoiceType={createInvoiceType}
        // Pre-fill from transaction data
        initialPartnerId={selectedPartner.length === 1 ? selectedPartner[0] : null}
        initialDescription={
          transaction?.description ||
          `${createInvoiceType === "bill" ? "Bill" : "Invoice"} for transaction`
        }
        initialAmount={
          transaction ? Math.abs(parseFloat(transaction.amount || 0)) : null
        }
        // Use transaction date as issue date and transaction ID as invoice number
        initialIssueDate={transaction?.transaction_date}
        initialInvoiceNumber={
          transaction?.transaction_id || transaction?.id?.toString()
        }
      />
    </Card>
  );
};

export default PaymentAllocation;
